// Room 顶部信息条 —— Room 名 / 连接状态 / 邀请 / 通知入口

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  getRoomNotifyMode,
  requestNotifyPermission,
  setRoomNotifyMode,
  type RoomNotifyMode,
} from "../lib/notification";
import { InviteModal } from "./InviteModal";

interface RoomHeaderProps {
  roomId: string;
  status: string;
  /** 竖屏成员抽屉触发 (可选, 桌面/横屏 RoomMembersPanel 常驻可忽略) */
  membersCount?: number;
  onMembersClick?: () => void;
}

export function RoomHeader({ roomId, status, membersCount, onMembersClick }: RoomHeaderProps) {
  const navigate = useNavigate();
  const [showInvite, setShowInvite] = useState(false);
  const [notifyMode, setNotifyModeState] = useState<RoomNotifyMode>(() =>
    getRoomNotifyMode(roomId),
  );
  const [notifyOpen, setNotifyOpen] = useState(false);

  useEffect(() => {
    setNotifyModeState(getRoomNotifyMode(roomId));
  }, [roomId]);

  const statusBadge =
    status === "open"
      ? "在线"
      : status === "connecting"
        ? "连接中…"
        : status === "reconnecting"
          ? "重连中…"
          : "已断开";

  return (
    <>
      <header className="room-header">
        <button
          className="room-header__back"
          onClick={() => navigate("/rooms")}
          aria-label="返回会话列表"
          title="返回"
        >
          ←
        </button>
        <div className="room-header__main">
          <h1 className="room-header__title"># {roomId}</h1>
          <div className="room-header__meta muted">WS {statusBadge}</div>
        </div>
        <div className="room-header__notify">
          <button
            className="room-header__action room-header__bell"
            onClick={async () => {
              if (notifyMode !== "muted") await requestNotifyPermission();
              setNotifyOpen((v) => !v);
            }}
            title={
              notifyMode === "all"
                ? "通知: 全部"
                : notifyMode === "mentions"
                  ? "通知: 仅 @我"
                  : "已静音"
            }
            aria-haspopup="menu"
            aria-expanded={notifyOpen}
          >
            {notifyMode === "muted" ? "🔕" : notifyMode === "mentions" ? "🔔!" : "🔔"}
          </button>
          {notifyOpen && (
            <>
              <div className="user-chip__scrim" onClick={() => setNotifyOpen(false)} />
              <menu className="user-chip__menu room-header__notify-menu">
                {(
                  [
                    ["all", "全部消息", "默认 · 任何新消息都响"],
                    ["mentions", "仅 @我", "只有点名才响"],
                    ["muted", "完全静音", "不响也不弹"],
                  ] as Array<[RoomNotifyMode, string, string]>
                ).map(([mode, label, desc]) => (
                  <button
                    type="button"
                    key={mode}
                    className={
                      notifyMode === mode
                        ? "user-chip__item is-active"
                        : "user-chip__item"
                    }
                    onClick={() => {
                      setRoomNotifyMode(roomId, mode);
                      setNotifyModeState(mode);
                      setNotifyOpen(false);
                    }}
                  >
                    <strong>{label}</strong>
                    <span className="room-header__notify-desc">{desc}</span>
                  </button>
                ))}
              </menu>
            </>
          )}
        </div>
        <button
          className="room-header__action"
          onClick={() => setShowInvite(true)}
          title="邀请他人加入此 Room"
        >
          ⊕ 邀请
        </button>
        {onMembersClick && (
          <button
            className="room-header__action room-header__members"
            onClick={onMembersClick}
            title="查看成员"
            aria-label="查看成员"
          >
            成员{typeof membersCount === "number" ? ` (${membersCount})` : ""}
          </button>
        )}
      </header>
      {showInvite && (
        <InviteModal roomId={roomId} onClose={() => setShowInvite(false)} />
      )}
    </>
  );
}
